import { useState, createContext, useContext, useEffect, useCallback } from "react";
import type { ReactNode } from "react";
import useRetailCollections from "../Hooks/useRetailCollections";
import useTarifs from "../Hooks/useTarifs";
import type { FilterItem } from './CatalogFilterContext';
import type { MinMaxPrice } from "../types";

type AllMinMax = Record<string, MinMaxPrice> | null;

const theContext = createContext<any>(undefined)

const RetailCatalogFilterContext = (() => useContext(theContext))

const RetailCatalogFilterContextProvider = ({ children }: { children: ReactNode }) => {
   const { getRetailGammesAsSortedArray, getRetailCollectionsAsSortedArray, getRetailGammeForCollectionId, getRetailFamillesAsSortedArray, getRetailTypologiesAsSortedArray, getRetailCyclesAsSortedArray, getAllRetailModelsAsArray } = useRetailCollections()
   const { getMinMaxPriceForEachModelInArray } = useTarifs()
   const [paramCollectionId, setParamCollectionId] = useState<string | null>(null)
   const [paramGammeId, setParamGammeId] = useState<string | null>(null)
   const [paramFamilleId, setParamFamilleId] = useState<string | null>(null)
   const [gammes, setGammes] = useState<FilterItem[] | null>(null)
   const [collections, setCollections] = useState<FilterItem[] | null>(null)
   const [familles, setFamilles] = useState<FilterItem[] | null>(null)
   const [typologies, setTypologies] = useState<FilterItem[] | null>(null)
   const [cycles, setCycles] = useState<FilterItem[] | null>(null)
   const [doFilter, setDoFilter] = useState(false)
   const [searchText, setSearchText] = useState('')
   const [minPrice, setMinPrice] = useState<number | null>(null)
   const [maxPrice, setMaxPrice] = useState<number | null>(null)
   const [minManualPrice, setMinManualPrice] = useState<number | null>(null)
   const [maxManualPrice, setMaxManualPrice] = useState<number | null>(null)
   const [allMinMax, setAllMinMax] = useState<AllMinMax>(null)
   const [excluBoutique, setExcluBoutique] = useState<string | null>(null)

   useEffect(() => {
      const gamme_id = paramGammeId ?? (paramCollectionId ? getRetailGammeForCollectionId(paramCollectionId)?.id : null)

      // console.log('+++GAMMES', gamme_id, paramCollectionId);
      setGammes(
         getRetailGammesAsSortedArray().map((gamme) => {
            return {
               id: gamme.id,
               label: gamme.label,
               visible: true,
               checked: (gamme_id === gamme.id)
            }
         })
      )
      // Déclencher le filtrage dans le même batch React que la mise à jour des gammes
      if (gamme_id) {
         setDoFilter(true)
      }
   }, [getRetailGammeForCollectionId, getRetailGammesAsSortedArray, paramCollectionId, paramGammeId, setDoFilter])

   useEffect(() => {
      setCollections(
         getRetailCollectionsAsSortedArray().map((collection) => {
            return {
               id: collection.id,
               label: collection.label,
               visible: true,
               checked: (paramCollectionId === collection.id)
            }
         })
      )
   }, [getRetailCollectionsAsSortedArray, paramCollectionId])

   useEffect(() => {
      setFamilles(
         getRetailFamillesAsSortedArray().map((famille) => {
            return {
               id: famille.id,
               label: famille.label,
               visible: true,
               checked: (paramFamilleId === famille.id)
            }
         })
      )
      if (paramFamilleId) {
         // Purger les autres filtres pour ne garder que la famille d'entrée
         setGammes(g => g?.map(gamme => ({ ...gamme, checked: false })))
         setCollections(c => c?.map(coll => ({ ...coll, checked: false })))
         setTypologies(t => t?.map(typo => ({ ...typo, checked: false })))
         setCycles(c => c?.map(cycle => ({ ...cycle, checked: false })))
         setDoFilter(true)
      }
   }, [getRetailFamillesAsSortedArray, paramFamilleId, setDoFilter, setGammes, setCollections, setTypologies, setCycles])

   useEffect(() => {
      setTypologies(
         getRetailTypologiesAsSortedArray().map((typo) => {
            return {
               id: typo.id,
               label: typo.label,
               visible: true,
               checked: false
            }
         })
      )
   }, [getRetailTypologiesAsSortedArray])

   useEffect(() => {
      setCycles(
         getRetailCyclesAsSortedArray().map((cycle) => {
            return {
               id: cycle.id,
               label: cycle.label,
               visible: true,
               checked: false
            }
         })
      )
   }, [getRetailCyclesAsSortedArray, setCycles])

   /*******
    ******* RÉCUPÉRATION DES ITEMS SÉLECTIONNÉS (GAMMES, COLLECTIONS, FAMILLES)
    *******/

   const getSelectedGammes = useCallback(() => {
      // console.log('+++getSelectedGammes:', gammes?.map(g => `${g.id}:${g.checked}`))
      return gammes?.filter((gamme) => gamme.checked)
   }, [gammes])

   const getSelectedGammesIds = useCallback((): string[] => {
      const _gammes = getSelectedGammes()
      return _gammes?.map(gamme => gamme.id) ?? []
   }, [getSelectedGammes])

   const getSelectedCollections = useCallback(() => {
      return collections?.filter((collection) => collection.checked && collection.visible)
   }, [collections])

   const getSelectedCollectionsIds = useCallback((): string[] => {
      const _collections = getSelectedCollections()
      return _collections?.map((collection) => collection.id) ?? []
   }, [getSelectedCollections])

   const getSelectedFamilles = useCallback(() => {
      return familles?.filter((famille) => famille.checked && famille.visible)
   }, [familles])

   const getSelectedFamillesIds = useCallback((): string[] => {
      const _familles = getSelectedFamilles()
      return _familles?.map((famille) => famille.id) ?? []
   }, [getSelectedFamilles])

   const getSelectedTypologies = useCallback(() => {
      return typologies?.filter((typo) => typo.checked && typo.visible)
   }, [typologies])

   const getSelectedTypologiesIds = useCallback((): string[] => {
      const _typologies = getSelectedTypologies()
      return _typologies?.map((typo) => typo.id) ?? []
   }, [getSelectedTypologies])

   const getSelectedCycles = useCallback(() => {
      return cycles?.filter((cycle) => cycle.checked && cycle.visible)
   }, [cycles])

   const getSelectedCyclesIds = useCallback((): string[] => {
      const _cycles = getSelectedCycles()
      return _cycles?.map((cycle) => cycle.id) ?? []
   }, [getSelectedCycles])

   const clearGammes = useCallback(() => {
      // console.log('ON GAMME CLEAR')
      const arr = gammes!.map((gamme) => {
         return { ...gamme, checked: false }
      })
      setGammes(arr)
   }, [gammes])

   const clearCollections = useCallback(() => {
      const arr = collections!.map((collection) => {
         return { ...collection, checked: false }
      })
      setCollections(arr)
   }, [collections])

   const clearFamilles = useCallback(() => {
      const arr = familles!.map((famille) => {
         return { ...famille, checked: false }
      })
      setFamilles(arr)
   }, [familles])

   const clearTypologies = useCallback(() => {
      const arr = typologies!.map((typo) => {
         return { ...typo, checked: false }
      })
      setTypologies(arr)
   }, [typologies])

   const clearCycles = useCallback(() => {
      const arr = cycles!.map((cycle) => {
         return { ...cycle, checked: false }
      })
      setCycles(arr)
   }, [cycles])

   const clearExcluBoutique = useCallback(() => {
      setExcluBoutique(null)
   }, [])

   const clearAllFilters = useCallback(() => {
      clearGammes()
      clearCollections()
      clearFamilles()
      clearTypologies()
      clearCycles()
      clearExcluBoutique()
      setMinManualPrice(null)
      setMaxManualPrice(null)
   }, [clearCollections, clearCycles, clearFamilles, clearGammes, clearTypologies, clearExcluBoutique])

   // Calcul des min/max de prix pour tous les modèles retail
   useEffect(() => {
      const models = getAllRetailModelsAsArray()
      if (models?.length) {
         const ret = getMinMaxPriceForEachModelInArray(models)
         setAllMinMax(ret)
      }
   }, [getAllRetailModelsAsArray, getMinMaxPriceForEachModelInArray])

   return (
      <theContext.Provider value={{ doFilter, setDoFilter, searchText, setSearchText, paramCollectionId, setParamCollectionId, paramGammeId, setParamGammeId, paramFamilleId, setParamFamilleId, gammes, setGammes, collections, setCollections, familles, setFamilles, typologies, setTypologies, cycles, setCycles, excluBoutique, setExcluBoutique, getSelectedGammes, getSelectedGammesIds, getSelectedCollections, getSelectedCollectionsIds, getSelectedFamilles, getSelectedFamillesIds, getSelectedTypologies, getSelectedTypologiesIds, getSelectedCyclesIds, clearGammes, clearCollections, clearTypologies, clearFamilles, clearCycles, clearExcluBoutique, clearAllFilters, allMinMax, minPrice, setMinPrice, maxPrice, setMaxPrice, minManualPrice, setMinManualPrice, maxManualPrice, setMaxManualPrice }}>
         {children}
      </theContext.Provider>
   )
}

export { RetailCatalogFilterContext, RetailCatalogFilterContextProvider }
